import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { usePrints, useDeletePrint, usePrintFilamentUsage } from '../../hooks/usePrints'
import { usePrinters } from '../../hooks/usePrinters'
import { useBuilds } from '../../hooks/useBuilds'
import { useFilamentSpools } from '../../hooks/useFilamentSpools'
import { formatMoney, formatDate, monthKey, emojiForProduct } from '../../lib/format'
import type { Print, Build } from '../../lib/database.types'
import { Skeleton, SwipeRow } from '../../components/ui'

export default function PrintsList() {
  const { data: prints, isLoading } = usePrints()
  const { data: printers } = usePrinters()
  const { data: builds } = useBuilds()
  const { data: spools } = useFilamentSpools()
  const { data: filamentUsages } = usePrintFilamentUsage()
  const deletePrint = useDeletePrint()

  // Map spoolId → color_hex for swatches
  const spoolColorHexById = new Map<string, string>(
    (spools ?? []).map((s) => [s.id, s.color_hex ?? '#888888'])
  )

  // Map printId → spool ids used on that print
  const printToSpoolIds = new Map<string, string[]>()
  for (const usage of filamentUsages ?? []) {
    const existing = printToSpoolIds.get(usage.print_id) ?? []
    existing.push(usage.spool_id)
    printToSpoolIds.set(usage.print_id, existing)
  }

  if (isLoading) return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  const printerById = new Map((printers ?? []).map((p) => [p.id, p.name]))
  const buildById = new Map((builds ?? []).map((b) => [b.id, b]))
  const groups = groupByMonth(prints ?? [])

  // Summary stats by purpose
  const all = prints ?? []
  const purposeBuckets: Array<{ label: string; purpose: Print['purpose']; color: string }> = [
    { label: 'For-sale', purpose: 'business-sale', color: 'var(--color-success)' },
    { label: 'Test', purpose: 'business-test', color: 'var(--color-warning)' },
    { label: 'Personal', purpose: 'personal', color: 'var(--color-text-muted)' },
    { label: 'Gifts', purpose: 'gift', color: 'var(--color-text-muted)' },
  ]
  const summaryRows = purposeBuckets.map((b) => {
    const list = all.filter((p) => p.purpose === b.purpose)
    return {
      ...b,
      count: list.length,
      cost: list.reduce((sum, p) => sum + Number(p.total_cost), 0),
      hours: list.reduce((sum, p) => sum + Number(p.print_time_hours), 0),
    }
  })
  const totalCount = all.length
  const totalCost = all.reduce((sum, p) => sum + Number(p.total_cost), 0)
  const totalHours = all.reduce((sum, p) => sum + Number(p.print_time_hours), 0)

  return (
    <div>
      {totalCount > 0 && (
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          marginBottom: 'var(--space-4)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-1)' }}>
            <span style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-faint)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: 600,
            }}>
              All-time totals
            </span>
            <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
              {formatMoney(totalCost)}
            </span>
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
            {totalCount} {totalCount === 1 ? 'print' : 'prints'} · {totalHours.toFixed(1)}h total
          </div>
          {summaryRows.filter((r) => r.count > 0).map((r) => (
            <div
              key={r.purpose}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-1) 0',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: r.color,
                }} />
                {r.label}
                <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-xs)' }}>
                  · {r.count}{r.count === 1 ? ' print' : ' prints'} · {r.hours.toFixed(1)}h
                </span>
              </span>
              <span style={{ fontWeight: 600 }}>{formatMoney(r.cost)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Link
          to="/prints/new"
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
          }}
        >
          + Log print
        </Link>
      </div>

      {Object.keys(groups).length === 0 ? (
        <div
          style={{
            padding: 'var(--space-8)',
            textAlign: 'center',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-text-muted)',
          }}
        >
          No prints yet — tap &quot;Log print&quot; after your next print finishes.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {Object.entries(groups)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([month, list]) => {
              const monthCost = list.reduce((sum, p) => sum + Number(p.total_cost), 0)
              const clusters = clusterPrints(list, buildById)
              return (
                <div key={month} style={{ marginBottom: 'var(--space-6)' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-surface-2)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 600,
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    <span>{monthLabel(month)}</span>
                    <span>
                      {list.length} print{list.length === 1 ? '' : 's'} · {formatMoney(monthCost)} cost
                    </span>
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {clusters.map((c, i) =>
                      c.kind === 'cluster' ? (
                        <BuildClusterRow
                          key={c.build?.id ?? `c${i}`}
                          cluster={c}
                          printerById={printerById}
                          printToSpoolIds={printToSpoolIds}
                          spoolColorHexById={spoolColorHexById}
                          onDelete={(id) => deletePrint.mutate(id)}
                        />
                      ) : (
                        <SinglePrintRow
                          key={c.prints[0].id}
                          print={c.prints[0]}
                          printerById={printerById}
                          onDelete={(id) => deletePrint.mutate(id)}
                        />
                      ),
                    )}
                  </ul>
                </div>
              )
            })}
        </div>
      )}
    </div>
  )
}

// ============ Clustering ============
//
// Plates that share a build (multi-plate builds like a 4-plate Sonic Hedgehog)
// get rendered together inside a single visual card so it's obvious which
// plate belongs to which finished thing. Standalone prints (single-plate
// builds or detached prints) render as their own card.

interface PrintCluster {
  kind: 'cluster' | 'single'
  prints: Print[]   // always sorted oldest→newest for cluster display
  build: Build | null
  sortDate: string  // representative date for chronological ordering within the month
}

function clusterPrints(
  prints: Print[],
  buildById: Map<string, Build>,
): PrintCluster[] {
  // Bucket by build_id; detached prints stay standalone.
  const byBuild = new Map<string, Print[]>()
  const standalone: Print[] = []
  for (const p of prints) {
    if (p.build_id) {
      const list = byBuild.get(p.build_id) ?? []
      list.push(p)
      byBuild.set(p.build_id, list)
    } else {
      standalone.push(p)
    }
  }

  const result: PrintCluster[] = []
  for (const [buildId, list] of byBuild) {
    if (list.length > 1) {
      // Multi-plate build → one cluster
      const sorted = [...list].sort((a, b) => a.created_at.localeCompare(b.created_at))
      // Sort the whole cluster by its most-recent plate so it sits in the
      // right chronological position within the month list.
      const sortDate = sorted.reduce(
        (max, p) => (p.print_date > max ? p.print_date : max),
        sorted[0].print_date,
      )
      result.push({
        kind: 'cluster',
        prints: sorted,
        build: buildById.get(buildId) ?? null,
        sortDate,
      })
    } else {
      // Single-plate build → render the same as a standalone print, but keep
      // the build reference so the row can show its build name.
      standalone.push(...list)
    }
  }
  for (const p of standalone) {
    result.push({
      kind: 'single',
      prints: [p],
      build: p.build_id ? buildById.get(p.build_id) ?? null : null,
      sortDate: p.print_date,
    })
  }

  result.sort((a, b) => b.sortDate.localeCompare(a.sortDate))
  return result
}

// ============ Row components ============

function BuildClusterRow({
  cluster,
  printerById,
  printToSpoolIds,
  spoolColorHexById,
  onDelete,
}: {
  cluster: PrintCluster
  printerById: Map<string, string>
  printToSpoolIds: Map<string, string[]>
  spoolColorHexById: Map<string, string>
  onDelete: (id: string) => void
}) {
  // Collapsed by default — the build is the canonical unit on the prints page;
  // individual plates are an inside detail you expand into.
  const [expanded, setExpanded] = useState(false)
  const totalCost = cluster.prints.reduce((sum, p) => sum + Number(p.total_cost), 0)
  const totalHours = cluster.prints.reduce((sum, p) => sum + Number(p.print_time_hours), 0)
  const buildName = cluster.build?.name ?? `Build ${cluster.build?.id.slice(0, 6) ?? '?'}`
  const status = cluster.build?.status ?? 'in_progress'

  // Collect unique spool colors across all plates in the cluster
  const uniqueSpoolIds = Array.from(
    new Set(
      cluster.prints.flatMap((p) => printToSpoolIds.get(p.id) ?? [])
    )
  )

  // Per-plate tick: true if the plate has at least one filament usage
  const plateTicks = cluster.prints.map((p) => (printToSpoolIds.get(p.id)?.length ?? 0) > 0)

  const statusChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    ...(status === 'completed'
      ? { background: 'var(--color-success-soft)', color: 'var(--color-success-text)' }
      : status === 'abandoned'
      ? { background: 'var(--color-danger-soft)', color: 'var(--color-danger-text)' }
      : { background: 'var(--color-warning-soft)', color: 'var(--color-warning-text)' }),
  }

  return (
    <li
      style={{
        background: 'var(--color-surface)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-2)',
        borderLeft: '3px solid var(--color-primary)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: 'inherit',
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
        }}
        aria-expanded={expanded}
      >
        {/* Header row: thumbnail + name + status chip + cost */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: expanded ? 'var(--space-2)' : 'var(--space-1)',
            borderBottom: expanded ? '1px solid var(--color-border)' : 'none',
            gap: 'var(--space-2)',
          }}
        >
          {/* Product thumbnail */}
          <div
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #4f46e5, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              color: 'white',
              userSelect: 'none',
            }}
          >
            {emojiForProduct(buildName)}
          </div>

          {/* Name + meta */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 12,
                  color: 'var(--color-text-faint)',
                  fontSize: 'var(--font-size-xs)',
                  transition: 'transform 150ms',
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transformOrigin: 'center',
                  flexShrink: 0,
                }}
              >
                ▸
              </span>
              <span style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {buildName}
              </span>
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginLeft: 16, marginTop: 1 }}>
              {cluster.prints.length} plate{cluster.prints.length === 1 ? '' : 's'} · {totalHours.toFixed(1)}h
            </div>
          </div>

          {/* Status chip (floats top-right) */}
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span style={statusChipStyle}>
              {status === 'completed' ? '✓ done' : status === 'abandoned' ? '✗ abandoned' : '⏳ in progress'}
            </span>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{formatMoney(totalCost)}</div>
          </div>
        </div>

        {/* Color swatches + per-plate progress ticks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-1)', flexWrap: 'wrap' }}>
          {/* Filament color swatches */}
          {uniqueSpoolIds.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {uniqueSpoolIds.map((sid) => (
                <div
                  key={sid}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: spoolColorHexById.get(sid) ?? '#888',
                    border: '1px solid rgba(0,0,0,0.15)',
                    flexShrink: 0,
                  }}
                  title={spoolColorHexById.get(sid)}
                />
              ))}
              <span style={{ fontSize: 10, color: 'var(--color-text-faint)', marginLeft: 2 }}>
                · {uniqueSpoolIds.length} color{uniqueSpoolIds.length === 1 ? '' : 's'}
              </span>
            </div>
          )}

          {/* Per-plate progress ticks */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: uniqueSpoolIds.length > 0 ? 'var(--space-1)' : 0 }}>
            {plateTicks.map((hasFil, idx) => (
              <div
                key={idx}
                style={{
                  width: 12,
                  height: 4,
                  borderRadius: 2,
                  background: hasFil ? 'var(--color-success)' : 'var(--color-border)',
                }}
                title={hasFil ? `Plate ${idx + 1}: filament logged` : `Plate ${idx + 1}: no filament yet`}
              />
            ))}
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', marginTop: 'var(--space-2)' }}>
          {cluster.prints.map((p) => (
            <PlateSubRow
              key={p.id}
              print={p}
              printerById={printerById}
              onDelete={onDelete}
            />
          ))}
          {cluster.build && (
            <Link
              to={`/builds/${cluster.build.id}/bulk-fill`}
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: 'var(--space-2)',
                padding: 'var(--space-2)',
                border: '1px dashed var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              ⚡ Bulk fill filament for all plates →
            </Link>
          )}
        </div>
      )}
    </li>
  )
}

function PlateSubRow({
  print: p,
  printerById,
  onDelete,
}: {
  print: Print
  printerById: Map<string, string>
  onDelete: (id: string) => void
}) {
  const navigate = useNavigate()
  return (
    <SwipeRow
      onEdit={() => navigate(`/prints/${p.id}/edit`)}
      onDelete={() => onDelete(p.id)}
      deleteConfirmText="Delete this print? Filament weight will be restored to the spool(s)."
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--space-2)',
          background: 'var(--color-surface-2)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.description || p.purpose}
            {p.units_produced > 1 && ` (${p.units_produced}×)`}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
            {formatDate(p.print_date)} · {printerById.get(p.printer_id) ?? 'unknown'} · {p.print_time_hours}h ·{' '}
            <span style={{ color: outcomeColor(p.outcome), fontWeight: 600 }}>{p.outcome}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
            {formatMoney(Number(p.total_cost))}
          </div>
        </div>
      </div>
    </SwipeRow>
  )
}

function SinglePrintRow({
  print: p,
  printerById,
  onDelete,
}: {
  print: Print
  printerById: Map<string, string>
  onDelete: (id: string) => void
}) {
  const navigate = useNavigate()
  return (
    <li
      style={{
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-2)',
        overflow: 'hidden',
      }}
    >
      <SwipeRow
        onEdit={() => navigate(`/prints/${p.id}/edit`)}
        onDelete={() => onDelete(p.id)}
        deleteConfirmText="Delete this print? Filament weight will be restored to the spool(s)."
      >
        <div
          style={{
            background: 'var(--color-surface)',
            padding: 'var(--space-3)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {p.description || p.purpose}{' '}
                {p.units_produced > 1 && `(${p.units_produced}×)`}{' '}
                <span
                  style={{
                    marginLeft: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-sm)',
                    background: outcomeColor(p.outcome),
                    color: 'white',
                  }}
                >
                  {p.outcome}
                </span>
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-faint)',
                }}
              >
                {formatDate(p.print_date)} · {printerById.get(p.printer_id) ?? 'unknown printer'} · {p.print_time_hours}h
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700 }}>{formatMoney(Number(p.total_cost))}</div>
            </div>
          </div>
        </div>
      </SwipeRow>
    </li>
  )
}

function groupByMonth(prints: Print[]): Record<string, Print[]> {
  const groups: Record<string, Print[]> = {}
  for (const p of prints) {
    const key = monthKey(p.print_date)
    if (!groups[key]) groups[key] = []
    groups[key].push(p)
  }
  return groups
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

function outcomeColor(outcome: string): string {
  if (outcome === 'success') return 'var(--color-success)'
  if (outcome === 'partial-fail') return 'var(--color-warning)'
  return 'var(--color-danger)'
}
